// Prerendered `/` — the marketing homepage, and the site's flagship SEO surface (its term is "ai journal"). Node-prerender-safe: MUI + react-router + the shared splash modules + literals only, no store/Amplify/@gnothi/shared/config imports, and no window/localStorage/document at module scope — this module's whole graph is eagerly imported during the Node prerender pass (see series.tsx's header). No loader, ever: the page is static literals.
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import { Link as RouterLink } from 'react-router';
import { splashGradient } from '../../src/ui/Setup/Mui';
import { SplashChrome, AUTH_SIGN_UP } from './splash-chrome';
import { Band, CtaBand } from './splash-sections';
import { metaDescription, splashMeta, softwareApplicationLd, ORIGIN } from './series-ld';
import { HomeInterconnect } from './home-interconnect';
import { HomePillars } from './home-pillars';
import { HomeMethod, HomeAudiences, HomePricing, HomeComplianceNotice } from './home-sections';
import { useHomeShims } from './home-shims';
import type { Route } from './+types/home';

const TITLE = 'Gnothi — the AI journal that writes the book of your life';
const DESCRIPTION = metaDescription(
  'Gnothi is an AI journal: write entries, track what matters, and let it author your story - chapter by chapter, in your own words.',
);
const PATH = '/';

export const meta: Route.MetaFunction = () => [
  ...splashMeta({ title: TITLE, description: DESCRIPTION, path: PATH }),
  {
    'script:ld+json': softwareApplicationLd({
      name: 'Gnothi',
      description: DESCRIPTION,
      url: `${ORIGIN}${PATH}`,
      applicationCategory: 'LifestyleApplication',
      featureList: [
        'AI journal that authors your entries into an ongoing Series',
        'Free automatic narration of every Chapter, published as a podcast feed',
        'Custom Fields with light scoring and causal analysis',
        'Tag-scoped sharing with three privacy tiers, and Groups',
      ],
    }),
  },
];

const PILLAR_CHIPS = [
  { label: 'Series', to: '/series', icon: <MenuBookOutlinedIcon /> },
  { label: 'Fields', to: '/fields', icon: <InsightsOutlinedIcon /> },
  { label: 'Community', to: '/community', icon: <PeopleOutlinedIcon /> },
];

/** One claim, one proof, one action, then a fold rail carrying the pillar chips and the scroll cue. The theme's `h1` is a flat 4.5rem with no breakpoints (Mui.tsx), which renders at 72px on a 360px phone, so the ramp lives here — page typography, not a primitive edit. */
function Hero() {
  return (
    <Stack sx={{ alignItems: 'center', textAlign: 'center', maxWidth: 900, mx: 'auto', width: '100%' }}>
      <Typography variant="overline" sx={{ color: 'primary.light', letterSpacing: 1.5, fontWeight: 700 }}>
        From the creator of Habitica
      </Typography>
      {/* No `color` — it INHERITS the band's `common.white`; see the Chip note below for why `primary.contrastText` is not an option here either. */}
      <Typography variant="h1" component="h1" sx={{ fontSize: { xs: '2.5rem', sm: '3.25rem', md: '4rem', lg: '4.5rem' }, lineHeight: 1.05, mt: 1.5 }}>
        Write the book of your life with{' '}
        <Box component="span" sx={{ color: 'primary.light', display: 'inline-block' }}>Gnothi</Box>
      </Typography>
      <Typography sx={{ color: 'grey.300', fontWeight: 300, fontSize: { xs: '1.125rem', md: '1.375rem' }, maxWidth: 680, mt: 2.5 }}>
        An AI journal that reads what you actually write and authors it back to you as an ongoing book - a Chapter at a time, narrated for you free.
      </Typography>
      <Button
        variant="contained"
        size="large"
        component={RouterLink}
        to={AUTH_SIGN_UP}
        endIcon={<ArrowForwardIosIcon sx={{ fontSize: '1rem' }} />}
        sx={{ ...splashGradient.secondary, mt: 4, py: 1.5, px: 5, fontSize: '1.125rem', borderRadius: '50px' }}
      >
        Get started free
      </Button>
      <Typography variant="body2" sx={{ mt: 1.5, color: 'grey.500' }}>
        Free to start • No credit card required
      </Typography>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        sx={{ alignItems: 'center', justifyContent: 'center', width: '100%', mt: { xs: 5, md: 8 }, pt: 3, borderTop: '1px solid', borderColor: 'rgba(255,255,255,0.14)' }}
      >
        <Stack direction="row" sx={{ flexWrap: 'wrap', justifyContent: 'center', gap: 1 }}>
          {PILLAR_CHIPS.map((c) => (
            // `common.white`, never `primary.contrastText`: a Chip paints its own `text.primary` (Chip.js:83) so the color must be spelled, and the contrastText token is navy in the dark scheme — on the navy wash.
            <Chip key={c.label} component={RouterLink} to={c.to} clickable icon={c.icon} label={c.label}
              sx={{ color: 'common.white', borderColor: 'primary.light', px: 0.5, '& .MuiChip-icon': { color: 'primary.light' } }}
              variant="outlined" />
          ))}
        </Stack>
        <Button href="#interconnect" endIcon={<KeyboardArrowDownIcon />} sx={{ color: 'grey.400' }}>
          See how the three fit together
        </Button>
      </Stack>
    </Stack>
  );
}

export default function Home() {
  useHomeShims();
  return (
    <SplashChrome active="home">
      <Band color="dark" hero><Hero /></Band>
      <Band id="interconnect" color="grey"><HomeInterconnect /></Band>
      <Band id="pillars" color="light"><HomePillars /></Band>
      <Band id="method" color="grey"><HomeMethod /></Band>
      {/* `#who` is the page's one dark accent band (hero + at most two, per the vocabulary). It is the ONLY band here that can safely take `dark`: its whole body is `FeatureCard`s, whose opaque `background.paper` survives the navy wash, and `SectionHeader`'s deck flips its own muted role — no `text.primary` span and no translucent Card to re-color. `#interconnect`/`#method`/`#pricing` all carry raw Cards, a Paper or restated muted prose, so a flip there would ship near-black-on-navy. */}
      <Band id="who" color="dark"><HomeAudiences /></Band>
      <Band id="pricing" color="grey"><HomePricing /></Band>
      <CtaBand
        color="light"
        title="Start with one entry"
        deck="Write today, and Gnothi has something to author from. Free to start, no credit card, and every Chapter narrates itself."
      />
      <HomeComplianceNotice />
    </SplashChrome>
  );
}
